import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { dmnIdentity } from "@/utils/dmnIdentity";
import { getEnvironmentProps } from "@/utils/environment";
import {
  fetchScanHistorySummary,
  ScanHistorySummary
} from "@/services/ai/ScanHistoryApi";

interface UseScanHistorySummaryOptions {
  venueId: string | undefined;
  from?: string;
  to?: string;
}

interface UseScanHistorySummaryResult {
  summary: ScanHistorySummary | null;
  isLoading: boolean;
}

export const useScanHistorySummary = ({
  venueId,
  from,
  to
}: UseScanHistorySummaryOptions): UseScanHistorySummaryResult => {
  const [summary, setSummary] = useState<ScanHistorySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);

    if (!venueId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const token = await dmnIdentity.getAccessToken();
        const env = getEnvironmentProps(token);

        if (!env?.CORE_API_BASE_URL || !token) {
          return;
        }

        const data = await fetchScanHistorySummary(
          venueId,
          from,
          to,
          env.CORE_API_BASE_URL,
          token
        );

        if (!cancelled) {
          setSummary(data);
        }
      } catch (error) {
        console.warn("[ScanHistory] Failed to load summary:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [venueId, from, to]);

  return { summary, isLoading };
};

export const useWeeklyScanHistorySummary = (
  venueId: string | undefined
): UseScanHistorySummaryResult => {
  // Intentionally static: dates are computed once per mount so the weekly
  // range stays stable for the lifetime of the component.
  const from = useMemo(
    () => dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    []
  );
  const to = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  return useScanHistorySummary({ venueId, from, to });
};

export const useAllTimeScanHistorySummary = (
  venueId: string | undefined
): UseScanHistorySummaryResult => {
  return useScanHistorySummary({ venueId });
};
